import React from "react";

// =============================================================================
// MINI SPARKLINE — Inline SVG area chart for bento cards
// =============================================================================
// PURPOSE: Tiny area sparkline rendered as pure SVG. No chart library.
// Shows daily trends at a glance — transaction volume, dial count, etc.
// =============================================================================

interface MiniSparklineProps {
  /** Array of numeric values (one per time unit, e.g. daily counts) */
  data: number[];
  /** SVG width */
  width?: number;
  /** SVG height */
  height?: number;
  /** Fill color for the area (supports CSS color values) */
  color?: string;
  /** Stroke color — defaults to same as fill but more opaque */
  strokeColor?: string;
}

const MiniSparkline: React.FC<MiniSparklineProps> = ({
  data,
  width = 120,
  height = 28,
  color = "rgba(59, 130, 246, 0.15)",
  strokeColor,
}) => {
  if (data.length < 2) return null;

  const max = Math.max(...data, 1);
  const pad = 1; // small padding so peaks don't clip

  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - pad - (val / max) * (height - pad * 2);
    return `${x},${y}`;
  });

  const linePath = `M${points.join(" L")}`;
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  const effectiveStroke = strokeColor || color.replace(/[\d.]+\)$/, "0.6)");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      style={{ display: "block" }}
    >
      <path d={areaPath} fill={color} />
      <path
        d={linePath}
        fill="none"
        stroke={effectiveStroke}
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default MiniSparkline;
